import { useTranslation } from 'react-i18next'
import { Shield, FileText, Lock, AlertTriangle, ExternalLink, Download } from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Hero } from '@/components/Hero'
import { Section } from '@/components/Section'
import { getMediaUrl } from '@/utils/media'

export function WhistleblowingPage() {
  const { t, i18n } = useTranslation()
  const reportingUrl = i18n.language === 'it'
    ? 'https://whistlesblow.it/c/chemical-newtech-spa/1'
    : 'https://whistlesblow.it/c/chemical-newtech-spa/2'

  return (
    <>
      <SEO
        title={t('whistleblowing.metaTitle')}
        description={t('whistleblowing.metaDescription')}
      />

      <Hero
        title={t('whistleblowing.heroTitle')}
        subtitle={t('whistleblowing.heroSubtitle')}
        size="sm"
      />

      <Section>
        <div className="mx-auto max-w-4xl">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-secondary-600">
              {t('whistleblowing.introText')}
            </p>
          </div>

          {/* Key Features */}
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <div className="flex gap-4 rounded-lg border border-secondary-200 bg-white p-6">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900">{t('whistleblowing.feature1Title')}</h3>
                <p className="mt-1 text-sm text-secondary-600">{t('whistleblowing.feature1Text')}</p>
              </div>
            </div>
            <div className="flex gap-4 rounded-lg border border-secondary-200 bg-white p-6">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900">{t('whistleblowing.feature2Title')}</h3>
                <p className="mt-1 text-sm text-secondary-600">{t('whistleblowing.feature2Text')}</p>
              </div>
            </div>
            <div className="flex gap-4 rounded-lg border border-secondary-200 bg-white p-6">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900">{t('whistleblowing.feature3Title')}</h3>
                <p className="mt-1 text-sm text-secondary-600">{t('whistleblowing.feature3Text')}</p>
              </div>
            </div>
            <div className="flex gap-4 rounded-lg border border-secondary-200 bg-white p-6">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                <AlertTriangle className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900">{t('whistleblowing.feature4Title')}</h3>
                <p className="mt-1 text-sm text-secondary-600">{t('whistleblowing.feature4Text')}</p>
              </div>
            </div>
          </div>

          {/* What to Report */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-secondary-900">{t('whistleblowing.whatToReportTitle')}</h2>
            <ul className="mt-4 list-inside list-disc space-y-2 text-secondary-600">
              <li>{t('whistleblowing.report1')}</li>
              <li>{t('whistleblowing.report2')}</li>
              <li>{t('whistleblowing.report3')}</li>
              <li>{t('whistleblowing.report4')}</li>
              <li>{t('whistleblowing.report5')}</li>
            </ul>
          </div>

          {/* How to Report */}
          <div className="mt-12 rounded-lg bg-primary-50 p-8">
            <h2 className="text-2xl font-bold text-secondary-900">{t('whistleblowing.howToReportTitle')}</h2>
            <p className="mt-4 text-secondary-600">{t('whistleblowing.howToReportText')}</p>
            <div className="mt-6">
              <a
                href={reportingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium text-primary hover:underline"
              >
                {t('whistleblowing.reportingPlatform')}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Documentation Downloads */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-secondary-900">{t('whistleblowing.documentsTitle')}</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <a
                href={getMediaUrl('documents/whistleblower-policy-it.pdf')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg border border-secondary-200 bg-white p-4 hover:border-primary transition-colors"
              >
                <Download className="h-5 w-5 flex-shrink-0 text-primary" />
                <span className="text-sm font-medium text-secondary-900">{t('whistleblowing.downloadPolicy')}</span>
              </a>
              <a
                href={getMediaUrl('documents/procedura-segnalazioni-wb.pdf')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg border border-secondary-200 bg-white p-4 hover:border-primary transition-colors"
              >
                <Download className="h-5 w-5 flex-shrink-0 text-primary" />
                <span className="text-sm font-medium text-secondary-900">{t('whistleblowing.downloadProcedure')}</span>
              </a>
            </div>
          </div>

          {/* Legal Framework */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-secondary-900">{t('whistleblowing.legalTitle')}</h2>
            <p className="mt-4 text-secondary-600">{t('whistleblowing.legalText')}</p>
          </div>
        </div>
      </Section>
    </>
  )
}
